import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useReveal } from "../../hooks/useReveal";
import { easing } from "../../lib/animations";

/**
 * Showreel section.
 * - By default the section is FROZEN on the B&W poster frame.
 * - "Play Showreel" toggles playback of the SALOMON music video.
 * - When the section scrolls out of view, the video resets back to the poster
 *   (paused + currentTime = 0) so each entry feels like a fresh ignition.
 * - Crosshair cursor / 3×3 rule-of-thirds grid retained.
 */
const SHOWREEL_SOURCES = [
    { src: "/assets/yk/showreel-main.mp4", type: "video/mp4" },
];
const POSTER = "/assets/yk/showreel-poster.jpg";

export const Showreel = () => {
    const [ref, inView] = useReveal({ threshold: 0.2 });
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [cursor, setCursor] = useState({ x: 50, y: 50 });
    const [playing, setPlaying] = useState(false);
    const [hoverPlay, setHoverPlay] = useState(false);

    // Cursor tracking — INSTANT, no transition
    useEffect(() => {
        const onMove = (e) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;
            if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
                setCursor({ x, y });
            }
        };
        const c = containerRef.current;
        if (c) c.addEventListener("mousemove", onMove);
        return () => {
            if (c) c.removeEventListener("mousemove", onMove);
        };
    }, []);

    // Reset on scroll-out: pause and rewind so the section returns to its frozen frame
    useEffect(() => {
        if (inView) return;
        const v = videoRef.current;
        if (!v) return;
        try {
            if (!v.paused) v.pause();
            v.currentTime = 0;
        } catch (e) {
            /* ignore — metadata may not be ready */
        }
        setPlaying(false);
    }, [inView]);

    const togglePlay = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        const v = videoRef.current;
        if (!v) return;
        if (playing) {
            try {
                v.pause();
            } catch (err) {
                /* ignore */
            }
            setPlaying(false);
        } else {
            v.muted = false;
            const promise = v.play();
            if (promise && typeof promise.then === "function") {
                promise.then(() => setPlaying(true)).catch(() => {
                    v.muted = true;
                    const fallback = v.play();
                    if (fallback && typeof fallback.then === "function") {
                        fallback.then(() => setPlaying(true)).catch(() => {});
                    }
                });
            } else {
                setPlaying(true);
            }
        }
    };

    return (
        <section
            ref={ref}
            data-testid="showreel-section"
            id="showreel"
            style={{
                position: "relative",
                width: "100%",
                minHeight: "100vh",
                background: "#000",
                color: "#fff",
                overflow: "hidden",
            }}
        >
            <div
                ref={containerRef}
                onClick={togglePlay}
                style={{
                    position: "relative",
                    width: "100%",
                    height: "100vh",
                    minHeight: 700,
                    cursor: "none",
                }}
            >
                {/* Poster image — visible when paused */}
                <img
                    src={POSTER}
                    alt=""
                    aria-hidden="true"
                    data-testid="showreel-poster"
                    draggable={false}
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "grayscale(1) contrast(1.04) brightness(0.78)",
                        zIndex: 1,
                        opacity: playing ? 0 : 1,
                        transition: "opacity 0.7s cubic-bezier(0.65, 0, 0.35, 1)",
                        pointerEvents: "none",
                    }}
                />

                {/* Video — kept mounted; visible only while playing */}
                <video
                    ref={videoRef}
                    muted
                    playsInline
                    loop
                    preload="metadata"
                    poster={POSTER}
                    data-testid="showreel-video"
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "grayscale(0.4) contrast(1.06) brightness(0.78)",
                        zIndex: 2,
                        opacity: playing ? 1 : 0,
                        transition: "opacity 0.7s cubic-bezier(0.65, 0, 0.35, 1)",
                        pointerEvents: "none",
                    }}
                >
                    {SHOWREEL_SOURCES.map((s) => (
                        <source key={s.src} src={s.src} type={s.type} />
                    ))}
                </video>

                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "radial-gradient(ellipse at center, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0.65) 100%)",
                        zIndex: 3,
                        pointerEvents: "none",
                    }}
                />

                {/* 3x3 grid overlay (rule-of-thirds) */}
                <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 4, pointerEvents: "none" }}>
                    <div style={{ position: "absolute", top: 0, bottom: 0, left: "33.333%", width: 1, background: "rgba(255,255,255,0.18)" }} />
                    <div style={{ position: "absolute", top: 0, bottom: 0, left: "66.666%", width: 1, background: "rgba(255,255,255,0.18)" }} />
                    <div style={{ position: "absolute", left: 0, right: 0, top: "33.333%", height: 1, background: "rgba(255,255,255,0.18)" }} />
                    <div style={{ position: "absolute", left: 0, right: 0, top: "66.666%", height: 1, background: "rgba(255,255,255,0.18)" }} />
                </div>

                {/* Crosshair lines — INSTANT */}
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: `${cursor.x}%`,
                        width: 1,
                        background: "rgba(255,255,255,0.55)",
                        zIndex: 5,
                        pointerEvents: "none",
                    }}
                />
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: `${cursor.y}%`,
                        height: 1,
                        background: "rgba(255,255,255,0.55)",
                        zIndex: 5,
                        pointerEvents: "none",
                    }}
                />

                {/* Cursor square marker */}
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        left: `${cursor.x}%`,
                        top: `${cursor.y}%`,
                        width: hoverPlay ? 28 : 12,
                        height: hoverPlay ? 28 : 12,
                        marginLeft: hoverPlay ? -14 : -6,
                        marginTop: hoverPlay ? -14 : -6,
                        background: "rgba(255,255,255,0.85)",
                        zIndex: 6,
                        pointerEvents: "none",
                        transition: "width 0.18s ease, height 0.18s ease, margin 0.18s ease",
                    }}
                />

                {/* Reference marker — fixed bottom-right thirds intersection */}
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        left: "66.666%",
                        top: "66.666%",
                        width: 8,
                        height: 8,
                        marginLeft: -4,
                        marginTop: -4,
                        background: "rgba(255,255,255,0.45)",
                        zIndex: 6,
                        pointerEvents: "none",
                    }}
                />

                {/* Cursor-tracking "Play / Pause" label — sits to the right of the square marker */}
                <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.9, ease: easing.enter, delay: 0.2 }}
                    onClick={togglePlay}
                    onMouseEnter={() => setHoverPlay(true)}
                    onMouseLeave={() => setHoverPlay(false)}
                    data-testid="showreel-play-button"
                    style={{
                        position: "absolute",
                        left: `${cursor.x}%`,
                        top: `${cursor.y}%`,
                        transform: "translate(22px, -50%)",
                        zIndex: 7,
                        background: "transparent",
                        border: "none",
                        color: "#fff",
                        fontFamily: "Manrope, sans-serif",
                        fontSize: 14,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        cursor: "none",
                        padding: "6px 12px",
                        whiteSpace: "nowrap",
                        pointerEvents: "none",
                        textShadow: "0 0 8px rgba(0,0,0,0.55)",
                    }}
                >
                    {playing ? "Pause" : "Play Showreel"}
                </motion.button>

                {/* Top-left small label */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={inView ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.7, delay: 0.4, ease: easing.primary }}
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        top: 24,
                        left: 32,
                        zIndex: 7,
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 10,
                        letterSpacing: "0.24em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.75)",
                        pointerEvents: "none",
                    }}
                >
                    Field 002 — Proof Showreel
                </motion.div>

                {/* Bottom-left status */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={inView ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.7, delay: 0.5, ease: easing.primary }}
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        bottom: 24,
                        left: 32,
                        zIndex: 7,
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 10,
                        letterSpacing: "0.24em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.75)",
                        pointerEvents: "none",
                    }}
                >
                    {playing ? "Signal · LIVE" : "Signal · standby"}
                </motion.div>

                {/* Bottom-right hint */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={inView ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.7, delay: 0.6, ease: easing.primary }}
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        bottom: 24,
                        right: 32,
                        zIndex: 7,
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 10,
                        letterSpacing: "0.24em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.75)",
                        pointerEvents: "none",
                    }}
                >
                    Click anywhere — {playing ? "Pause" : "Ignite"}
                </motion.div>
            </div>
        </section>
    );
};

export default Showreel;
